import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Phone, Star, MapPin, CreditCard, CheckCircle2, Sparkles, Clock, Shield, UserPlus, Upload, FileText } from "lucide-react";

const StepCard = ({ icon: Icon, num, title, body }) => (
  <div className="bg-white border border-[#E8E2D6] rounded-2xl p-6 relative">
    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97757]">Step {num}</div>
    <Icon className="w-5 h-5 text-[#0F766E] mt-3" strokeWidth={1.5} />
    <h3 className="font-serif-display text-xl mt-3">{title}</h3>
    <p className="text-sm text-[#5F6C60] leading-relaxed mt-2">{body}</p>
  </div>
);

const TermPill = ({ term, def }) => (
  <div className="bg-[#F9F7F3] border border-[#E8E2D6] rounded-xl p-4">
    <div className="font-serif-display text-lg">{term}</div>
    <p className="text-sm text-[#5F6C60] mt-1 leading-relaxed">{def}</p>
  </div>
);

export default function HowItWorks() {
  const [audience, setAudience] = useState("customers");

  return (
    <div className="max-w-5xl mx-auto px-6 py-16">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">Guide</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight mt-2">How Massage Savings works</h1>
      <p className="text-[#5F6C60] mt-3 max-w-2xl leading-relaxed">
        A flat-fee marketplace where massage therapists keep <span className="font-semibold text-[#2C352D]">100%</span> of every deal they sell. No commissions. No cuts. Just better local prices for you.
      </p>

      <Tabs value={audience} onValueChange={setAudience} className="mt-10">
        <TabsList className="bg-white border border-[#E8E2D6] rounded-full p-1">
          <TabsTrigger data-testid="how-tab-customers" value="customers" className="rounded-full px-6">For Customers</TabsTrigger>
          <TabsTrigger data-testid="how-tab-therapists" value="therapists" className="rounded-full px-6">For Therapists & Businesses</TabsTrigger>
        </TabsList>

        {/* ============ CUSTOMERS ============ */}
        <TabsContent value="customers" className="mt-10">
          <h2 className="font-serif-display text-3xl tracking-tight">Find massage deals near you in 4 steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
            <StepCard num="1" icon={Search} title="Browse or search" body="Use Browse Deals to filter by ZIP code, city, category (Swedish, Deep Tissue, Hot Stone, Prenatal, Sports, etc.), and price range." />
            <StepCard num="2" icon={MapPin} title="Pick a deal" body="Tap any deal to see full details — what's included, location, the therapist's profile, and verified customer reviews." />
            <StepCard num="3" icon={Phone} title="Contact the therapist directly" body="Call, email, or book through their site — whatever they offer. Massage Savings doesn't take a cut, so you pay the therapist directly at their listed deal price." />
            <StepCard num="4" icon={Star} title="Leave a review" body="After your session, log in and leave a star rating + comment. Honest reviews help your community find great therapists." />
          </div>

          <h2 className="font-serif-display text-3xl tracking-tight mt-14">What does the price mean?</h2>
          <div className="bg-white border border-[#E8E2D6] rounded-2xl p-6 mt-4">
            <p className="text-sm text-[#2C352D] leading-relaxed">
              Each deal shows two prices: the <span className="font-semibold">deal price</span> (what you pay) and the <span className="line-through text-[#5F6C60]">original price</span> (what the therapist usually charges).
              You pay the therapist directly using whatever method they accept — Massage Savings is just the listing service.
            </p>
          </div>

          <h2 className="font-serif-display text-3xl tracking-tight mt-14">Need a therapist but don't see one?</h2>
          <p className="text-[#5F6C60] mt-2 leading-relaxed">If you can't find what you're looking for in your area, fill out our <Link to="/need-therapist" className="text-[#D97757] hover:underline">Need a Therapist?</Link> form. Local therapists in our network will reach out with options.</p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/browse"><Button data-testid="how-cta-browse" className="rounded-full bg-[#D97757] hover:bg-[#C26243] text-white h-12 px-6">Browse deals now</Button></Link>
            <Link to="/need-therapist"><Button variant="outline" className="rounded-full h-12 px-6">Request a therapist</Button></Link>
          </div>
        </TabsContent>

        {/* ============ THERAPISTS ============ */}
        <TabsContent value="therapists" className="mt-10">
          <h2 className="font-serif-display text-3xl tracking-tight">List your massage deals — keep 100% of every sale</h2>
          <p className="text-[#5F6C60] mt-2 leading-relaxed max-w-3xl">
            Unlike other deal sites, we never take a cut. You pay a flat monthly listing fee per deal, and every dollar your customer pays goes to you. Cancel anytime.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
            <div className="bg-[#0F766E]/8 border border-[#0F766E]/30 rounded-2xl p-6">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0F766E]">Individual therapist</div>
              <div className="font-serif-display text-4xl mt-2">$49.99<span className="text-base text-[#5F6C60]"> / mo per deal</span></div>
              <p className="text-sm text-[#2C352D] mt-3 leading-relaxed">For solo practitioners. Pay only for the deals you publish.</p>
            </div>
            <div className="bg-[#D97757]/10 border border-[#D97757]/40 rounded-2xl p-6">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C26243]">Massage business / spa</div>
              <div className="font-serif-display text-4xl mt-2">$99.99<span className="text-base text-[#5F6C60]"> / mo per deal</span></div>
              <p className="text-sm text-[#2C352D] mt-3 leading-relaxed">For establishments with multiple therapists. Same flat-fee, no commissions.</p>
            </div>
          </div>

          <h2 className="font-serif-display text-3xl tracking-tight mt-14">Get listed in 5 simple steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
            <StepCard num="1" icon={UserPlus} title="Create an account" body="Sign up as Individual therapist or Massage business. Verify your email and add your contact info." />
            <StepCard num="2" icon={FileText} title="Submit your first deal" body="Click 'New deal' in your dashboard. Add title, description, original + deal price, category, location, and how customers should contact you (phone, email, or booking URL)." />
            <StepCard num="3" icon={Upload} title="Upload a photo (or pick stock)" body="Upload your own JPEG/PNG/WEBP image (up to 5 MB) for higher conversion, or pick from our curated stock photo library." />
            <StepCard num="4" icon={Clock} title="Get approved — go live free for 3 days" body="Our team reviews your listing within 24 hours. Once approved, it goes live immediately with a free 3-day trial. No card required upfront." />
            <StepCard num="5" icon={CreditCard} title="Subscribe to keep it published" body="After the 3-day trial, subscribe to publish for 30 more days. Stripe handles payment securely. Renew, pause, or delete anytime." />
          </div>

          <h2 className="font-serif-display text-3xl tracking-tight mt-14">Optional: pin to top with Featured</h2>
          <div className="bg-white border border-[#E8E2D6] rounded-2xl p-6 mt-4 flex items-start gap-4">
            <Sparkles className="w-6 h-6 text-[#D97757] mt-1 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="text-sm text-[#2C352D] leading-relaxed">
                Stand out in search by pinning your deal to the top for <span className="font-semibold">$19 for 7 days</span>. Click "Feature 7d · $19" on any of your live deals from the dashboard. One-time charge — no recurring fee.
              </p>
            </div>
          </div>

          <h2 className="font-serif-display text-3xl tracking-tight mt-14">Customer interaction</h2>
          <p className="text-[#5F6C60] mt-2 leading-relaxed">
            Customers contact you directly using the redemption methods you choose (phone, email, booking URL). Massage Savings never inserts itself between you and your client.
            You handle scheduling, payment, and the customer relationship — exactly like a regular client.
          </p>

          <h2 className="font-serif-display text-3xl tracking-tight mt-14">Glossary — what every term means</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <TermPill term="Pending review" def="You submitted the deal; our team is reviewing it. Usually approved within 24 hours." />
            <TermPill term="Trial" def="Approved deal, live free for 3 days. Subscribe before the trial ends to keep it published." />
            <TermPill term="Active" def="Subscription paid. Deal is live for 30 days from payment date." />
            <TermPill term="Subscribe to relist" def="Your trial or 30-day window ended. One click + payment to re-publish for another 30 days." />
            <TermPill term="Featured" def="Pinned to the top of search results for 7 days. Costs $19 one-time per deal." />
            <TermPill term="Suspended" def="Hidden from public view, usually because of a policy violation. Contact us if you think it's a mistake." />
            <TermPill term="Permanently free" def="A special status granted by the platform owner — the deal stays live without any subscription. Reserved for partnerships." />
            <TermPill term="Redemption methods" def="How customers contact you to book: phone call, email, or a booking URL on your site." />
          </div>

          <h2 className="font-serif-display text-3xl tracking-tight mt-14">Payment security</h2>
          <div className="bg-white border border-[#E8E2D6] rounded-2xl p-6 mt-4 flex items-start gap-4">
            <Shield className="w-6 h-6 text-[#0F766E] mt-1 shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-[#2C352D] leading-relaxed">
              All subscription payments are processed by <span className="font-semibold">Stripe</span> — the same payments backbone used by Lyft, Shopify, and millions of businesses.
              We never see or store your card details.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register"><Button data-testid="how-cta-register" className="rounded-full bg-[#D97757] hover:bg-[#C26243] text-white h-12 px-6">Create therapist account</Button></Link>
            <Link to="/faq"><Button variant="outline" className="rounded-full h-12 px-6">See FAQ</Button></Link>
          </div>

          <div className="mt-12 p-6 bg-[#F9F7F3] border border-dashed border-[#E8E2D6] rounded-2xl text-center">
            <CheckCircle2 className="w-6 h-6 text-[#0F766E] inline mr-2" strokeWidth={1.5} />
            <span className="text-sm text-[#2C352D]">
              Got more questions? <Link to="/contact" className="text-[#D97757] hover:underline font-semibold">Contact us</Link> — we reply within 1 business day.
            </span>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
